import { useMemo, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Select } from '../../components/ui/Input';
import { useCampaignAssets, useDeactivateCampaignAsset } from '../../hooks/useCampaignAssets';
import { CampaignAssetEditor } from './CampaignAssetEditor';
import {
  CAMPAIGN_SOURCES,
  buildCampaignLink,
  type CampaignAsset,
  type CampaignSourceId,
} from '../../api/campaignAssets';

/** Lemlist test campaigns ("TESTING", "Test – …") are hidden from the picker; the count says so. */
const TEST_CAMPAIGN = /^\s*(\[?test(ing)?\]?)(\b|\s|[-–:_]|$)/i;

/** "https://www.sequesto.com/en/guide?utm_source=…" → "sequesto.com/en/guide". */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    return `${u.hostname.replace(/^www\./, '')}${path}`;
  } catch {
    return url;
  }
}

function AssetRow({
  asset,
  link,
  signalLabels,
  isAdmin,
  onEdit,
  onRemove,
}: {
  asset: CampaignAsset;
  link: string;
  signalLabels: Map<string, string>;
  isAdmin: boolean;
  onEdit: (a: CampaignAsset) => void;
  onRemove: (a: CampaignAsset) => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is blocked outside a secure context or without permission.
      // Say so rather than leaving a button that silently does nothing.
      window.prompt('Copy the link manually:', link);
    }
  };

  return (
    <li className="group py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <a
            href={asset.url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="text-body-md font-medium text-text-primary hover:underline"
          >
            {asset.title}
          </a>
          {asset.description && (
            <p className="mt-0.5 line-clamp-2 text-body-sm text-text-tertiary" title={asset.description}>
              {asset.description}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {asset.signalTypes.length === 0 ? (
              <Badge variant="default">any signal</Badge>
            ) : (
              asset.signalTypes.map((s) => (
                <Badge key={s}>
                  {signalLabels.get(s) ?? s}
                </Badge>
              ))
            )}
            {asset.nurtureEligible && <Badge>nurture</Badge>}
          </div>
        </div>

        {/* Copy link last, so it sits flush right on every row; the hover-only
            Edit / Remove sit to its left instead of pushing it inwards. */}
        <div className="ml-auto flex flex-none items-center justify-end gap-1.5 pt-0.5">
          {isAdmin && (
            <span className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
              <Button variant="ghost" size="sm" onClick={() => onEdit(asset)}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onRemove(asset)}>
                Remove
              </Button>
            </span>
          )}
          <Button variant="secondary" size="sm" onClick={copy} className="min-w-[6.5rem]">
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
      </div>

      {/* The tagged link is long; show where it goes and keep the full URL (UTMs and all) in the tooltip and on Copy. */}
      <p className="mt-1.5 flex min-w-0 items-center gap-1 text-body-xs text-text-muted" title={link}>
        <Link2 className="h-3 w-3 flex-none" aria-hidden />
        <span className="truncate">{shortUrl(link)}</span>
      </p>
    </li>
  );
}

/**
 * GTM — Campaign materials. The collateral a rep drops into an outbound
 * sequence, filtered by the signal that put the company in the campaign.
 *
 * Picking a campaign and a channel rewrites every link on screen, so the rep
 * copies one already tagged rather than hand-writing UTMs. Campaign names come
 * from lemlist itself, which is what stops `utm_campaign` becoming five
 * spellings of the same thing.
 */
export function CampaignMaterials({ isAdmin }: { isAdmin: boolean }) {
  const { data, isLoading, error } = useCampaignAssets();
  const deactivate = useDeactivateCampaignAsset();

  const [signalId, setSignalId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [source, setSource] = useState<CampaignSourceId>('lemlist');
  const [editing, setEditing] = useState<CampaignAsset | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const assets = data?.assets ?? [];
  const signals = data?.signals ?? [];
  const campaigns = data?.campaigns ?? [];
  // One option per name: lemlist keeps copies of a campaign under the same
  // name, and the link only carries the name. Test campaigns are a display
  // filter, never deleted.
  const { campaignNames, hiddenTests } = useMemo(() => {
    const names = new Set<string>();
    let tests = 0;
    for (const c of campaigns) {
      const name = c.name.trim();
      if (TEST_CAMPAIGN.test(name)) tests += 1;
      else if (name) names.add(name);
    }
    return { campaignNames: [...names].sort((a, b) => a.localeCompare(b)), hiddenTests: tests };
  }, [campaigns]);

  const signalLabels = useMemo(
    () => new Map(signals.map((s) => [s.id, s.label])),
    [signals],
  );

  // An asset with no signals suits any campaign, so it always shows.
  const visible = useMemo(
    () =>
      signalId
        ? assets.filter((a) => a.signalTypes.length === 0 || a.signalTypes.includes(signalId))
        : assets,
    [assets, signalId],
  );

  const medium = CAMPAIGN_SOURCES.find((s) => s.id === source)?.medium ?? 'email';

  const openAdd = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const onRemove = (asset: CampaignAsset) => {
    if (!window.confirm(`Remove "${asset.title}" from campaign materials?`)) return;
    deactivate.mutate(asset.id);
  };

  return (
    <Card padding="md">
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-body-sm font-medium text-text-primary">GTM — Campaign materials</h3>
          <p className="mt-0.5 text-body-xs text-text-tertiary">
            Pick the signal behind the campaign, then copy a tagged link straight into your
            sequence.
          </p>
        </div>
        {isAdmin && (
          <Button variant="secondary" onClick={openAdd}>
            Add asset
          </Button>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select size="sm" value={signalId} onChange={(e) => setSignalId(e.target.value)} aria-label="Filter by signal">
          <option value="">All signals</option>
          {signals.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </Select>

        <Select
          size="sm"
          value={campaignName}
          onChange={(e) => setCampaignName(e.target.value)}
          aria-label="Campaign for the link"
        >
          <option value="">No campaign tag</option>
          {campaignNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>

        <Select
          size="sm"
          value={source}
          onChange={(e) => setSource(e.target.value as CampaignSourceId)}
          aria-label="Channel the link goes out on"
        >
          {CAMPAIGN_SOURCES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </Select>
        {hiddenTests > 0 && (
          <span className="text-body-xs text-text-tertiary" title="Campaigns named TESTING or Test… are left out of the list">
            {hiddenTests} test campaign{hiddenTests === 1 ? '' : 's'} hidden
          </span>
        )}
      </div>

      {deactivate.error && (
        <Alert variant="error">Could not remove the asset: {deactivate.error.message}</Alert>
      )}

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      )}

      {error && <Alert variant="error">Failed to load campaign materials: {error.message}</Alert>}

      {!isLoading && !error && campaigns.length === 0 && (
        <Alert variant="info">
          No Lemlist campaigns have synced yet, so links can only be tagged with the signal. Run the
          Lemlist sync to get the campaign list.
        </Alert>
      )}

      {!isLoading && !error && visible.length === 0 && (
        <p className="py-6 text-center text-body-sm text-text-tertiary">
          {assets.length === 0
            ? isAdmin
              ? 'No campaign materials yet. Add the first asset to get started.'
              : 'No campaign materials yet.'
            : 'No asset is tagged for this signal.'}
        </p>
      )}

      {visible.length > 0 && (
        <ul className="divide-y divide-border-light">
          {visible.map((asset) => (
            <AssetRow
              key={asset.id}
              asset={asset}
              link={buildCampaignLink({
                url: asset.url ?? '',
                assetSlug: asset.slug,
                source,
                medium,
                campaignName: campaignName || null,
                signalId: signalId || null,
              })}
              signalLabels={signalLabels}
              isAdmin={isAdmin}
              onEdit={(a) => {
                setEditing(a);
                setEditorOpen(true);
              }}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}

      {isAdmin && (
        <CampaignAssetEditor
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          asset={editing}
          signals={signals}
        />
      )}
    </Card>
  );
}
